import { Injectable } from '@angular/core';
import { IFilterSelectItem } from '../../shared/sample-tracking-select/sample-tracking-select.component';
import { ProtocolFilterData, SampleTrackingSampleCountRequest, FilterTypeEnum, SelectedFilter, ProtocolSponsorPairs, FilterNames, DbFilterNames } from '../../dashboard/overview-dashboard/dashboard-info';
import { includes, cloneDeep } from 'lodash';
import { comparators } from '../../../core';
import { ConfirmDialogRequest, ConfirmDialogComponent, SHIPMENT_CONFIRM_TOKEN } from '../../../site-shipping-tool/shared/confirm-dialog/confirm-dialog.component';
import { IMdlCustomDialogConfiguration, MdlDialogService, MdlDialogReference, MdlSnackbarComponent } from '@angular-mdl/core';
import { Observable } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { GroupTrackingGridData, QueryFilter, Operator, DynamicListRequestInfo } from '../filter-samples-info';
import * as moment from 'moment';
import { GlobalErrorHandler } from '../../../core/global-error-handler';
import { DateRangeFilterValue } from '../../../shared/time-frame-filter/timeframe-filter.component';
import { GridHeader, GridEvent } from '../../tracking-overview/interfaces/grid';
import { DynamicListViewColumnData, DynamicListColumnInfo } from '../interface/dynamic-list-info';
import { SampleColumnsService } from '../column/sample-column-service';
import { ColumnSettingInfo } from '../../../shared/covance-grid/column-settings/column-settings';
import { ColumnSettingsService } from '../../../shared/covance-grid/column-settings/service/column-settings.service';
import { SampleTrackerData, StudyGroupInfo } from '../../group-info/study-group-info';
import { DataChangeModelComponent, DATA_CHANGE_MODEL_TOKEN, DateChangeModel } from '../../shared/data-change-model/data-change-model.component';

@Injectable()
export class SamplesHelperService {

    public listViewConfirmDialogRef: MdlDialogReference;
    public isMissMatchOccured = false;
    private snackBar: Observable<MdlSnackbarComponent>;
    
    constructor(
        private readonly dialogService: MdlDialogService,
        private sampleColumnService: SampleColumnsService,
        private globalErrorHandler: GlobalErrorHandler,
        private columnSettingsService: ColumnSettingsService) {
    }

    public getFilterData(
        protocol: IFilterSelectItem[], countriesFilter: IFilterSelectItem[], regionFilter: IFilterSelectItem[], projectFilter: IFilterSelectItem[], invNumberFilter: IFilterSelectItem[], dataList: ProtocolFilterData[]) {
        const protocolDefined = (protocol && protocol.length > 0);
        const countryFilterDefined = countriesFilter && countriesFilter.length > 0;
        const regionDefined = regionFilter && regionFilter.length > 0;
        const projectFilterDefined = projectFilter && projectFilter.length > 0;
        const invFilterDefined = invNumberFilter && invNumberFilter.length > 0;
        const sitesFiltered = dataList.filter(s =>
            (!protocolDefined || includes(protocol.map(t => t.value), s.protocolCode))
            &&
            (!regionDefined || includes(regionFilter.map(t => t.value), s.region))
            &&
            (!countryFilterDefined || includes(countriesFilter.map(t => t.value), s.countryName))
            &&
            (!projectFilterDefined || includes(projectFilter.map(t => t.value), s.projectNumber))
            &&
            (!invFilterDefined || includes(invNumberFilter.map(t => t.value), s.investigatorNumber + ' - ' + s.investigatorName))
        );
        return sitesFiltered;
    }

    public filterDataByPatientNumber(patientInfo: string, data: GroupTrackingGridData[]) {
        if (!patientInfo || patientInfo.length <= 0) { return data; }
        return data.filter(s => s.patientnumber === patientInfo);
    }

    public getDropdownFilterData(portfolioData: ProtocolFilterData[], selectedItem: IFilterSelectItem[], searchText: string) {
        let filteredPortfolioData = portfolioData;
        if (selectedItem && selectedItem.length) {
            filteredPortfolioData = [];
            selectedItem.map(si => si.value)
                .forEach(name => {
                    filteredPortfolioData
                        .push(...portfolioData
                            .filter(sgpd => sgpd[searchText as keyof ProtocolFilterData] === name)
                        );
                });
        }
        return filteredPortfolioData;
    }

    public getFilteredItems(defaultList: IFilterSelectItem[], myFilterList: IFilterSelectItem[]) {
        let filteredList: IFilterSelectItem[] = [];
        if (!myFilterList || myFilterList.length === 0) { return filteredList; }
        if (!defaultList || defaultList.length === 0) { return filteredList; }
        myFilterList.forEach(t => {
            let index = defaultList.findIndex(item => item.id === t.id);
            if (index !== -1) {
                filteredList.push(t);
            }
        });
        return filteredList;
    }

    public loadSelectedFilterList(protocolFilters: IFilterSelectItem[], regionFilter: IFilterSelectItem[],
                                  projectFilter: IFilterSelectItem[], countryFilter: IFilterSelectItem[], invFilter: IFilterSelectItem[]) {
        let selectedFilterList: SelectedFilter[] = [];
        selectedFilterList = this.addSelectedItems(protocolFilters, FilterNames.protocol, selectedFilterList, DbFilterNames.protocol);
        selectedFilterList = this.addSelectedItems(regionFilter, FilterNames.region, selectedFilterList, DbFilterNames.region);
        selectedFilterList = this.addSelectedItems(projectFilter, FilterNames.project, selectedFilterList, DbFilterNames.project);
        selectedFilterList = this.addSelectedItems(countryFilter, FilterNames.country, selectedFilterList, DbFilterNames.country);
        selectedFilterList = this.addSelectedItems(invFilter, FilterNames.investigatorNum, selectedFilterList, DbFilterNames.investigatorNum);
        return selectedFilterList;
    }

    public addSelectedItems(filters: IFilterSelectItem[], label: string, selectedFilterList: SelectedFilter[], dbColumnName: string) {
        if (!filters || filters.length <= 0) { return selectedFilterList; }
        if (filters[0] === undefined) { return selectedFilterList; }
        const filterValues = selectedFilterList.filter(t => t.label === label);
        if (filterValues && filterValues.length > 0) {
            filterValues[0].selectedIds = filters.map(data => { return data.id; });
            filterValues[0].selectedValues = filters.map(data => { return data.value; });
            filterValues[0].filterItems = filters;
            return selectedFilterList;
        }
        const selectedFilter: SelectedFilter = {
            label: label,
            dbColumn: dbColumnName,
            selectedValues: filters.map(data => { return data.value; }),
            selectedIds: filters.map(data => { return data.id; }),
            secondaryIds: filters.map(data => { return data.sortId; }),
            filterItems: filters,
        };
        selectedFilterList.push(selectedFilter);
        return selectedFilterList;
    }

    public getWhereFilters(selectedFilters: SelectedFilter[], protocolFilters: IFilterSelectItem[], timeFrameFilter: DateRangeFilterValue, selectedTimeFrame: IFilterSelectItem) {
        let whereFilter: QueryFilter[] = [];
        selectedFilters = selectedFilters ? selectedFilters : [];
        selectedFilters.forEach(filter => {
            if (filter.dbColumn === 'subprotocol_region') {
                whereFilter.push({
                    filterName: filter.dbColumn,
                    value: filter.selectedValues.join(','),
                    operator: Operator.in
                });
            } else {
                whereFilter.push({
                    filterName: filter.dbColumn,
                    value: filter.selectedIds.join(','),
                    operator: Operator.in
                });
            }
        });
        if (selectedFilters.filter(t => t.dbColumn === DbFilterNames.protocol).length === 0) {
            whereFilter.push({
                filterName: DbFilterNames.protocol,
                value: protocolFilters.map(t => t.id).join(','),
                operator: Operator.in
            });
        }
        whereFilter.push({
            filterName: selectedTimeFrame.key,
            value: timeFrameFilter.dateBegin + ' 00:00 and ' + timeFrameFilter.dateEnd + ' 23:59',
            operator: Operator.between
        });
        return whereFilter;
    }

    public loadDropDownItems(filterData: ProtocolFilterData[], idField: string, valueField: string, extraField?: string) {
        let selectFilterData: IFilterSelectItem[] = [] = filterData.
            map((sites, index) => {
                return {
                    id: idField ? Number(`${sites[idField as keyof ProtocolFilterData]}`) : index + 1,
                    value: extraField ? `${sites[valueField as keyof ProtocolFilterData]}` + ' - ' + `${sites[extraField as keyof ProtocolFilterData]}` :
                        `${sites[valueField as keyof ProtocolFilterData]}`,
                    sortId: `${sites[valueField as keyof ProtocolFilterData]}`

                };
            }).filter(sfd => sfd.value !== 'null');
        if (!idField) {
            selectFilterData = selectFilterData.filter((data, i, arr) => arr.findIndex(t => t.value === data.value) === i);
        } else {
            selectFilterData = selectFilterData.filter((data, i, arr) => arr.findIndex(t => t.id === data.id) === i);
        }
        return this.sortFilterItems(selectFilterData);
    }

    public getSampleCountRequestData(
        protocol: IFilterSelectItem[], country: IFilterSelectItem[], project: IFilterSelectItem[], investigatorSite: IFilterSelectItem[], region: IFilterSelectItem[], overallInvFilters: IFilterSelectItem[]) {

        if (investigatorSite && investigatorSite.length > 0) {
            return this.getCountRequestData(investigatorSite, FilterTypeEnum.investigatorSite, true, investigatorSite);
        }
        if (country && country.length > 0) {
            return this.getCountRequestData(country, FilterTypeEnum.country, true, overallInvFilters);
        }
        if (project && project.length > 0) {
            return this.getCountRequestData(project, FilterTypeEnum.project, true, overallInvFilters);
        }
        if (region && region.length > 0) {
            return this.getCountRequestData(region, FilterTypeEnum.region, true, overallInvFilters);
        }
        if (protocol && protocol.length > 0) {
            return this.getCountRequestData(protocol, FilterTypeEnum.protocol, true, overallInvFilters);
        }
        return null;
    }

    public getCountRequestData(filterData: IFilterSelectItem[], filterType: FilterTypeEnum, loadInvSiteId = false, invFilters?: IFilterSelectItem[]) {
        var filterRequest: SampleTrackingSampleCountRequest = new SampleTrackingSampleCountRequest();
        filterRequest.filterType = filterType;
        filterRequest.filterId = filterData.map(filterInfo => filterInfo.id)
            .filter((data, i, arr) => arr.findIndex(t => t === data) === i && data !== 0);
        if (!loadInvSiteId || !invFilters) { return filterRequest; }
        filterRequest.investigatorSiteId = invFilters.map(filterInfo => filterInfo.id)
            .filter((data, i, arr) => arr.findIndex(t => t === data) === i);
        return filterRequest;
    }

    public closeConfirmDialog() {
        if (!this.listViewConfirmDialogRef) { return; }
        this.listViewConfirmDialogRef.hide();
    }

    public displayConfirmDialog(isProtocol = false): Observable<any> {

        let dialogRequest: ConfirmDialogRequest = {
            message: 'The requested data set is very large and is taking longer than 20 seconds to load <br> <br> We recommend that you generate a report for faster results on this data set. <br><br> Do you want to continue waiting?',
            yesButtonText: 'Yes, I want to wait',
            noButtonText: 'No, I want to cancel and run a report',
            isSampleTracking: true
        };

        return this.showConfirmDialog(dialogRequest).pipe(
            map((dialogRef) => {
                this.listViewConfirmDialogRef = dialogRef;
                return dialogRef;
            }),
            switchMap(dialog => dialog.onHide()),
            map(dialogResult => dialogResult));
    }

    public formatGridData(data: GroupTrackingGridData[], skipExpandData: boolean, columnHeaders: GridHeader[], isReceivedView = false, isMissMatchIconAppear = false) {
        let childrenData: GroupTrackingGridData[];
        let parentData: GroupTrackingGridData[];

        if (!skipExpandData) {
            childrenData = data.filter((sa: GroupTrackingGridData) => sa.parentBarcode) as GroupTrackingGridData[];
            parentData = data.filter((sa: GroupTrackingGridData) => !sa.parentBarcode) as GroupTrackingGridData[];
        }

        return data.map((gd: GroupTrackingGridData) => {
            gd.cssClass = '';
            if (isReceivedView) {
                this.formatDateTime(gd, columnHeaders);
            } else {
                this.formatDate(gd);
                this.formatTime(gd);
            }
            if (isMissMatchIconAppear) {
                this.setMissMatches(gd, columnHeaders);
            }
            if (!skipExpandData) {
                gd.expandData = { child: [], parent: [] };
                childrenData.forEach((cd: GroupTrackingGridData) => {
                    if ((cd.barcode && gd.parentBarcode) && cd.barcode === gd.parentBarcode) { // condition to handel grand parent
                        gd.expandData.parent.push(cd);
                        gd.cssClass = 'expand';
                    }
                    if ((cd.parentBarcode && gd.barcode) && cd.parentBarcode === gd.barcode) {
                        gd.expandData.child.push(cd);
                        gd.cssClass = 'expand';
                    }
                });
                parentData.forEach((pa: GroupTrackingGridData) => {
                    if ((pa.barcode && gd.parentBarcode) && pa.barcode === gd.parentBarcode) {
                        gd.expandData.parent.push(pa);
                        gd.cssClass = 'expand';
                    }
                });
            }

            return gd;
        });
    }

    public getProtocolFilterPairs(filterData: ProtocolFilterData[]): ProtocolSponsorPairs[] {
        if (!filterData || filterData.length === 0) { return []; }
        let protocolSponsorIds: ProtocolSponsorPairs[] = [] = filterData.
            map((data) => {
                return {
                    protocolId: data.protocolId,
                    protocolCode: data.protocolCode,
                    sponsorName: data.sponsorName,
                };
            }).
            filter((data, i, arr) => arr.findIndex(t => t.protocolId === data.protocolId) === i);
        return protocolSponsorIds;
    }

    public getSelectedFilterValue(label: string, selectedFilterList: SelectedFilter[], protocolFilters?: IFilterSelectItem[], isID: boolean = false) {
        if (!label) { return []; }
        const filter = selectedFilterList.filter(t => t.label === label);
        if (!filter || filter.length <= 0) {
            if (label !== FilterNames.protocol) { return []; }
            return protocolFilters ? protocolFilters.map(t => t.value) : [];
        }
        if (isID) { return filter[0].secondaryIds; }
        return filter[0].selectedValues;
    }

    public getSelectedFilterIds(label: string, selectedFilterList: SelectedFilter[], protocolFilters?: IFilterSelectItem[]) {
        if (!label) { return []; }
        const filter = selectedFilterList.filter(t => t.label === label);
        if (!filter || filter.length <= 0) {
            if (label !== FilterNames.protocol) { return []; }
            return protocolFilters ? protocolFilters.map(t => t.id) : [];
        }
        return filter[0].selectedIds;
    }

    public loadSqlProtocolData(protocolIdList: number[], protocolFilters: IFilterSelectItem[]) {
        let protocolInfoList: any[] = [];
        if (protocolFilters) {
            for (var protocol of protocolFilters) {
                if (protocolIdList.indexOf(protocol.id) === -1) { continue; }
                protocolInfoList.push({ protocolId: protocol.id, protocolCode: protocol.value });
            }
        }
        if (protocolInfoList.length === 0) {
            protocolInfoList.push({ protocolId: 0, protocolCode: 'not found' });
        }
        return protocolInfoList;
    }

    public alignBreadcrumb(position: string) {
        const breadcrumb = document.getElementById('app-breadcrumb');

        if (breadcrumb != null) {
            breadcrumb.style.top = position;
        }
    }

    public showNoDataText() {
        this.snackBar = this.globalErrorHandler.displayErrorMsg('Due to a technical issue, this data is not currently available. Please contact us.',
                                                                { handler: () => this.closeSnackbar(), text: 'DISMISS', isClosingAction: true });
    }

    public closeSnackbar() {
        if (!this.snackBar) { return; }
        this.snackBar.subscribe(t => t.hide());
    }

    public getDynamicListRequestInfo(queryFilters: QueryFilter[], columnDefs: GridHeader[], dynamicColumnInfo: DynamicListViewColumnData) {
        let columnInfo = this.sampleColumnService.getDynamicColumnList(columnDefs, dynamicColumnInfo.dynamicListColumnInfo);
        let dynamicListColumns: DynamicListRequestInfo = {
            dB_View_Name: dynamicColumnInfo.dbViewName,
            dB_View_Column_Name: columnInfo.columns,
            viewNaturalKey: dynamicColumnInfo.viewNaturalKey,
            joinTableFilter: columnInfo.joinColumns,
            customSql: columnInfo.customColumns,
            whereFilter: queryFilters,
        };
        return dynamicListColumns;
    }

    public getExportFileName(isProtocol: boolean, selectedProtocol: SampleTrackerData, selectedGroup: StudyGroupInfo, status: IFilterSelectItem) {
        return isProtocol ? 'Protocol ' + selectedProtocol.protocolCode + ' - ' + status.value :
            'Study Grouping ' + selectedGroup.groupName + ' - ' + status.value;
    }

    public openEditDialog(columnSettingInfo: ColumnSettingInfo): Observable<ColumnSettingInfo> {
        return this.columnSettingsService.openColumnSettingsPopup(columnSettingInfo).pipe(
            switchMap(dialog => dialog.onHide()),
            map((dialogResult: ColumnSettingInfo) => dialogResult || null)
        );
    }

    public openMisMatchModel($event: GridEvent, dynamicColumnData: DynamicListColumnInfo[]) {
        let dataModel: DateChangeModel = {
            data: $event.data,
            selectedColumn: $event.header,
            dyanmicData: dynamicColumnData.find(t => t.dbColumnName === $event.header.mismatchColumn)
        };
        const configDialog: IMdlCustomDialogConfiguration = {
            component: DataChangeModelComponent,
            providers: [{
                provide: DATA_CHANGE_MODEL_TOKEN, useValue: dataModel
            }],
            isModal: true,
            classes: 'st-report-delete-dialog',
            styles: { 'width' : '60%' },
            clickOutsideToClose: false,
        };
        return this.dialogService.showCustomDialog(configDialog);
    }

    private formatDate(gridData: GroupTrackingGridData) {
        gridData.specimenCollectionDate = gridData.specimenCollectionDate ? moment(gridData.specimenCollectionDate).format('DD-MMM-YYYY') : '';
        gridData.containerReceivedDate = gridData.containerReceivedDate ? moment(gridData.containerReceivedDate).format('DD-MMM-YYYY') : '';
        gridData.sampleRegisteredDate = gridData.sampleRegisteredDate ? moment(gridData.sampleRegisteredDate).format('DD-MMM-YYYY') : '';
        gridData.kitReceivedDate = gridData.kitReceivedDate ? moment(gridData.kitReceivedDate).format('DD-MMM-YYYY') : '';
        gridData.notification1Sent = gridData.notification1Sent ? moment(gridData.notification1Sent).format('DD-MMM-YYYY') : '';
        gridData.notification2Sent = gridData.notification2Sent ? moment(gridData.notification2Sent).format('DD-MMM-YYYY') : '';
        gridData.notification3Sent = gridData.notification3Sent ? moment(gridData.notification3Sent).format('DD-MMM-YYYY') : '';
        gridData.shippedDate = gridData.shippedDate ? moment(gridData.shippedDate).format('DD-MMM-YYYY') : '';
        gridData.shipmentSubmittedDate = gridData.shipmentSubmittedDate ? moment(gridData.shipmentSubmittedDate).format('DD-MMM-YYYY') : '';
        gridData.shipmentCreatedDate = gridData.shipmentCreatedDate ? moment(gridData.shipmentCreatedDate).format('DD-MMM-YYYY') : '';
        gridData.scheduledShipmentDate = gridData.scheduledShipmentDate ? moment(gridData.scheduledShipmentDate).format('DD-MMM-YYYY') : '';
        gridData.effectiveDate = gridData.effectiveDate ? moment(gridData.effectiveDate).format('DD-MMM-YYYY') : '';
        gridData.expirationDate = gridData.expirationDate ? moment(gridData.expirationDate).format('DD-MMM-YYYY') : '';
        gridData.eQueryOpenDate = gridData.eQueryOpenDate ? moment(gridData.eQueryOpenDate).format('DD-MMM-YYYY') : '';
    }

    private formatTime(gridData: GroupTrackingGridData) {
        gridData.specimenCollectionTime = gridData.specimenCollectionTime ? moment(gridData.specimenCollectionTime).format('HH:mm') : '';
        gridData.containerReceivedTime = gridData.containerReceivedTime ? moment(gridData.containerReceivedTime).format('HH:mm') : '';
        gridData.sampleRegisteredTime = gridData.sampleRegisteredTime ? moment(gridData.sampleRegisteredTime).format('HH:mm') : '';
        gridData.kitReceivedTime = gridData.kitReceivedTime ? moment(gridData.kitReceivedTime).format('HH:mm') : '';
        gridData.notification1SentTime = gridData.notification1SentTime ? moment(gridData.notification1SentTime).format('HH:mm') : '';
        gridData.notification2SentTime = gridData.notification2SentTime ? moment(gridData.notification2SentTime).format('HH:mm') : '';
        gridData.notification3SentTime = gridData.notification3SentTime ? moment(gridData.notification3SentTime).format('HH:mm') : '';
        gridData.shippedTime = gridData.shippedTime ? moment(gridData.shippedTime).format('HH:mm') : '';
        gridData.shipmentSubmittedTime = gridData.shipmentSubmittedTime ? moment(gridData.shipmentSubmittedTime).format('HH:mm') : '';
        gridData.shipmentCreatedTime = gridData.shipmentCreatedTime ? moment(gridData.shipmentCreatedTime).format('HH:mm') : '';
        gridData.scheduledShipmentTime = gridData.scheduledShipmentTime ? moment(gridData.scheduledShipmentTime).format('HH:mm') : '';
        gridData.eQueryOpenTime = gridData.eQueryOpenTime ? moment(gridData.eQueryOpenTime).format('HH:mm') : '';
    }
    private setMissMatches(gridData: GroupTrackingGridData | null, columnHeader: GridHeader[] | null) {
        if (!gridData) { return; }
        const matchValue = 'F';
        const investigatorName = 'investigatorname';
        const investigatorNumber = 'investigator_number';
        const visitName = 'visit_name';
        const protocolVisitCode = 'protocolvisitcode';
        if (!gridData.missMatches) {
            gridData.missMatches = [];
        }
        const existingColumnNames = new Set(gridData.missMatches.map(match => match.columnName));
        if (gridData.investigatornamematch === matchValue && !existingColumnNames.has(investigatorName)) {
            gridData.missMatches.push({ columnName: investigatorName });
            existingColumnNames.add(investigatorName);
            columnHeader = this.setMisMatchInfo(columnHeader, 'investigatornamematch', investigatorName);
            this.isMissMatchOccured = true;
        }
        if (gridData.investigatorsitenumbermatch === matchValue && !existingColumnNames.has(investigatorNumber)) {
            gridData.missMatches.push({ columnName: investigatorNumber });  
            existingColumnNames.add(investigatorNumber);
            columnHeader = this.setMisMatchInfo(columnHeader, 'investigatorsitenumbermatch', investigatorNumber);
            this.isMissMatchOccured = true;
        }
        if (gridData.visitnamematch === matchValue && !existingColumnNames.has(visitName)) {
            gridData.missMatches.push({ columnName: visitName });
            columnHeader = this.setMisMatchInfo(columnHeader, 'visitnamematch', visitName);
            existingColumnNames.add(visitName);
            this.isMissMatchOccured = true;
        }
        if (gridData.pvcmatch === matchValue && !existingColumnNames.has(protocolVisitCode)) {
            gridData.missMatches.push({ columnName: protocolVisitCode });
            existingColumnNames.add(protocolVisitCode);
            columnHeader = this.setMisMatchInfo(columnHeader, 'pvcmatch', protocolVisitCode);
            this.isMissMatchOccured = true;
        }
    }

    private formatDateTime(gridData: any, columnHeaders: GridHeader[]) {
        columnHeaders.filter(t => t.isTime).forEach(timeField => {
            gridData = this.formatDateTimeValue(gridData, timeField);
        });
        columnHeaders.filter(t => t.isDate).forEach((column: GridHeader) => {
            gridData = this.formatDateTimeValue(gridData, column);
        });
    }

    private showConfirmDialog(dialogRequest: ConfirmDialogRequest, showDeleteDialog = false) {

        const configDialog: IMdlCustomDialogConfiguration = {
            component: ConfirmDialogComponent,
            providers: [{
                provide: SHIPMENT_CONFIRM_TOKEN, useValue: dialogRequest
            }],
            isModal: true,
            classes: showDeleteDialog ? 'st-report-delete-dialog' : 'st-report-confirmation-dialog',
            styles: { 'border-radius': '30px' },
            clickOutsideToClose: false,
        };

        return this.dialogService.showCustomDialog(configDialog);
    }

    private sortFilterItems(filterItems: IFilterSelectItem[]) {
        return filterItems.sort((a, b) => comparators.mixedStringComparator(a.sortId, b.sortId));
    }

    private formatDateTimeValue(gridData: any, field: GridHeader) {
        let columnKey = field.isTime ? field.key.replace('_time', '') : field.key;
        const columnValue: string = gridData[columnKey as keyof GroupTrackingGridData];
        if (field.defaultDateTimeFormat && columnValue) {
            let format = !field.isTime ? field.defaultDateTimeFormat.toUpperCase() : field.defaultDateTimeFormat;
            gridData[field.key as keyof GroupTrackingGridData] = moment(columnValue).format(format);
        }
        return gridData;
    }

    private setMisMatchInfo(columnHeader: GridHeader[], mismatchName: string, key: string) {
        columnHeader.forEach(t => {
            if (t.key === key) {
                t.mismatchColumn = mismatchName;
            }
        });
        return columnHeader;
    }
}